/**
 * Report export utilities (PDF + Excel).
 *
 * PDF: builds a branded HTML document and triggers the browser print dialog
 *      (user saves as PDF). Excel: HTML table wrapped in a Blob (.xls).
 *
 * Design/layout copied from the RajFinances microfinance workspace so reports
 * look identical across the suite.
 */
import {
  COMPANY_DEFAULT_ADDRESS,
  COMPANY_DEFAULT_EMAIL,
  COMPANY_DEFAULT_PHONE,
  COMPANY_DEFAULT_WEBSITE,
  COMPANY_LEGAL_NAME,
  COMPANY_LOCATION,
  COMPANY_REPORT_TAGLINE,
} from './brand';
import { readCompanyProfile, companyComposedAddress } from './companyProfile';
import { mediaSrc } from './mediaUrl';
import { getPdfThemeColors } from './themeSettings';
import { Capacitor } from '@capacitor/core';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Resolve the active tenant's letterhead from the saved company profile (Settings →
 * Company), falling back to the product defaults when a field is blank. This makes
 * every exported report self-brand per workspace (SaaS model).
 */
export function resolveCompanyBrand() {
  const p = readCompanyProfile();
  const cityState = [p.city, p.state].map((s) => s.trim()).filter(Boolean).join(', ');
  const composedAddress = companyComposedAddress(p);
  return {
    name: (p.legalName || p.displayName).trim() || COMPANY_LEGAL_NAME,
    location: (p.displayName && p.legalName ? p.displayName : cityState).trim() || COMPANY_LOCATION,
    tagline: p.tagline.trim() || COMPANY_REPORT_TAGLINE,
    address: composedAddress || COMPANY_DEFAULT_ADDRESS,
    phone: [p.phone, p.altPhone].map((s) => s.trim()).filter(Boolean).join(' / ') || COMPANY_DEFAULT_PHONE,
    email: p.email.trim() || COMPANY_DEFAULT_EMAIL,
    website: p.website.trim() || COMPANY_DEFAULT_WEBSITE,
    logoUrl: mediaSrc(p.logoUrl),
    gstin: p.gstin.trim(),
  };
}

export type ExportRow = Record<string, string | number | boolean | null | undefined>;

function toHeadingCase(value: string) {
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

/** Escape text embedded into print HTML (title + header lines). */
function escapeHtmlAttr(s: string) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function slugFilename(title: string): string {
  return `${title.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '') || 'report'}.pdf`;
}

/** Portrait fits up to ~6 columns; landscape only when the table is genuinely wide. */
export function shouldUseLandscapePdf(rows: ExportRow[]): boolean {
  if (!rows.length) return false;
  const headers = Object.keys(rows[0]);
  const colCount = headers.length;
  if (colCount > 7) return true;

  const sample = rows.slice(0, 20);
  let maxCellLen = 0;
  let totalLen = 0;
  let cells = 0;
  for (const row of sample) {
    for (const h of headers) {
      const len = String(row[h] ?? '').length;
      maxCellLen = Math.max(maxCellLen, len);
      totalLen += len;
      cells += 1;
    }
  }
  const avgLen = cells ? totalLen / cells : 0;
  const maxHeaderLen = Math.max(...headers.map((h) => toHeadingCase(h).length), 0);

  if (colCount > 6) return true;
  if (colCount >= 5 && (maxCellLen > 36 || avgLen > 22)) return true;
  if (colCount >= 4 && maxHeaderLen > 16 && maxCellLen > 28) return true;
  return false;
}

function resolveLandscape(rows: ExportRow[], override?: boolean): boolean {
  if (override !== undefined) return override;
  return shouldUseLandscapePdf(rows);
}

function tableClassForLayout(landscape: boolean): string {
  return landscape ? 'mf-landscape-report' : 'mf-portrait-report';
}

/** Running inside the packaged Android/iOS app (Capacitor WebView) vs a browser. */
function isNativeApp(): boolean {
  return typeof window !== 'undefined' && Capacitor.isNativePlatform();
}

/**
 * Mobile environment (native app OR mobile browser). Used to pick the raster PDF
 * pipeline (mobile print engines mis-handle window.print()).
 */
function isMobileExportEnvironment(): boolean {
  if (typeof window === 'undefined') return false;
  if (Capacitor.isNativePlatform()) return true;
  const ua = navigator.userAgent || '';
  return /Android|iPhone|iPad|iPod|Mobile/i.test(ua);
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read file for export.'));
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(blob);
  });
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * Save a generated file and open it in the system viewer (PDF reader, Sheets…).
 * Does NOT open the social share sheet — that is only for WhatsApp share actions.
 *
 * - Native app: write to cache, then FileOpener (ACTION_VIEW → PDF apps).
 * - Browser: normal `<a download>`.
 */
export async function downloadFile(blob: Blob, filename: string): Promise<boolean> {
  if (isNativeApp()) {
    try {
      const { Filesystem, Directory } = await import('@capacitor/filesystem');
      const base64 = await blobToBase64(blob);
      const written = await Filesystem.writeFile({
        path: filename,
        data: base64,
        directory: Directory.Cache,
      });
      const mime = mimeForFilename(filename);
      try {
        const { FileOpener } = await import('@capacitor-community/file-opener');
        await FileOpener.open({
          filePath: written.uri,
          contentType: mime,
          openWithDefault: true,
        });
        return true;
      } catch (openErr) {
        // No PDF reader / opener failed — keep a Documents copy and tell the user.
        try {
          await Filesystem.writeFile({ path: filename, data: base64, directory: Directory.Documents });
        } catch {
          /* ignore */
        }
        const msg =
          openErr instanceof Error && /no activity|activity found/i.test(openErr.message)
            ? `Saved as ${filename}. Install a PDF reader app (e.g. Google Drive / Adobe), then open it from Files.`
            : `Saved as ${filename}. Open it from your Files / Downloads folder.`;
        window.alert(msg);
        return true;
      }
    } catch {
      // Fall through to the browser download strategy.
    }
  }
  downloadBlob(blob, filename);
  return true;
}

function mimeForFilename(filename: string): string {
  const lower = filename.toLowerCase();
  if (lower.endsWith('.pdf')) return 'application/pdf';
  if (lower.endsWith('.xls') || lower.endsWith('.xlsx')) {
    return 'application/vnd.ms-excel';
  }
  if (lower.endsWith('.csv')) return 'text/csv';
  return 'application/octet-stream';
}

/**
 * Share a generated file through the OS share sheet / Web Share API (WhatsApp,
 * Gmail, Save to Files…). Used only by the WhatsApp share action.
 */
export async function shareFile(blob: Blob, filename: string, mime: string): Promise<boolean> {
  if (isNativeApp()) {
    try {
      const [{ Filesystem, Directory }, { Share }] = await Promise.all([
        import('@capacitor/filesystem'),
        import('@capacitor/share'),
      ]);
      const base64 = await blobToBase64(blob);
      const written = await Filesystem.writeFile({ path: filename, data: base64, directory: Directory.Cache });
      await Share.share({ title: filename, files: [written.uri] });
      return true;
    } catch (err) {
      if (err instanceof Error && /cancel/i.test(err.message)) return true;
      // Fall through to browser strategies if the plugin call failed.
    }
  }

  const file = new File([blob], filename, { type: mime });
  const nav = typeof navigator !== 'undefined' ? (navigator as Navigator & { canShare?: (d: unknown) => boolean }) : null;
  if (nav?.share && (!nav.canShare || nav.canShare({ files: [file] }))) {
    try {
      await nav.share({ files: [file], title: filename });
      return true;
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return true;
    }
  }

  downloadBlob(blob, filename);
  return true;
}

/** Deliver a generated PDF from Export PDF / View — open in PDF reader on native. */
async function deliverExportPdf(blob: Blob, filename: string) {
  await downloadFile(blob, filename);
}

/**
 * Export rows as a real Excel workbook (.xls). The HTML-table `.xls` format opens
 * natively in Excel and Google Sheets on every platform — no CSV.
 */
export function exportExcel(filename: string, rows: ExportRow[]) {
  if (!rows.length) {
    window.alert('No data to export.');
    return;
  }
  const safeBase = filename.replace(/\.[^.]+$/, '') || 'report';
  const headers = Object.keys(rows[0]);
  const theme = getPdfThemeColors();
  const workbook = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
      <head><meta charset="utf-8" /><!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>Report</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]--></head>
      <body>
        <table border="1">
          <thead><tr>${headers.map((h) => `<th style="background:${theme.excelHeader};color:#fff;font-weight:bold;">${toHeadingCase(h)}</th>`).join('')}</tr></thead>
          <tbody>
            ${rows
              .map((row) => `<tr>${headers.map((h) => `<td>${escapeHtmlAttr(String(row[h] ?? ''))}</td>`).join('')}</tr>`)
              .join('')}
          </tbody>
        </table>
      </body>
    </html>
  `;
  const blob = new Blob(['\uFEFF', workbook], { type: 'application/vnd.ms-excel;charset=utf-8;' });
  void downloadFile(blob, `${safeBase}.xls`);
}

/**
 * Export a branded report as a real PDF file and download it directly — no print
 * dialog, no auto-open viewer. One pipeline for desktop + mobile so the font,
 * quality, and pagination are identical everywhere.
 * Returns a Promise so callers can show a generating animation.
 */
export function exportHtmlToPdf(
  title: string,
  tableHtml: string,
  opts?: { landscape?: boolean; compact?: boolean; singlePage?: boolean },
): Promise<void> {
  document.querySelectorAll('details[open]').forEach((el) => el.removeAttribute('open'));
  const landscape = Boolean(opts?.landscape);
  const prevCursor = document.body.style.cursor;
  document.body.style.cursor = 'wait';

  return buildReportPdfBlob(title, tableHtml, opts)
    .then((blob) => deliverExportPdf(blob, slugFilename(title)))
    .catch(() =>
      buildSimplePdfBlob(title, tableHtml, { landscape })
        .then((blob) => deliverExportPdf(blob, slugFilename(title)))
        .catch(() => {
          window.alert('Could not generate PDF on this device. Please try Export Excel.');
        }),
    )
    .finally(() => {
      document.body.style.cursor = prevCursor;
    });
}

/**
 * Rasterize a complete HTML document (no report letterhead wrap) to a single-page PDF.
 * Used for rectangular slips such as the finance payment receipt.
 */
export async function exportStandaloneHtmlToPdf(
  title: string,
  fullHtml: string,
  opts?: { widthPx?: number; captureSelector?: string; format?: 'a4' | 'a5' },
): Promise<void> {
  const prevCursor = document.body.style.cursor;
  document.body.style.cursor = 'wait';
  try {
    const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
      import('html2canvas'),
      import('jspdf'),
    ]);
    const widthPx = opts?.widthPx ?? 560;
    const iframe = await mountReportIframe(fullHtml, widthPx);
    const doc = iframe.contentDocument;
    if (!doc) {
      iframe.remove();
      throw new Error('Could not render document for PDF.');
    }
    const target =
      (opts?.captureSelector
        ? (doc.querySelector(opts.captureSelector) as HTMLElement | null)
        : null) || doc.body;
    const contentHeight = Math.max(target.scrollHeight, target.clientHeight, 400);
    iframe.style.height = `${Math.max(contentHeight + 40, 700)}px`;
    await new Promise<void>((resolve) => window.setTimeout(() => resolve(), 100));

    const dpr =
      typeof window !== 'undefined' && Number.isFinite(window.devicePixelRatio)
        ? Math.max(1, window.devicePixelRatio)
        : 1;
    const scale = Math.max(3, Math.min(5, 2 + dpr));
    const canvas = await html2canvas(target, {
      scale,
      useCORS: true,
      allowTaint: true,
      logging: false,
      backgroundColor: '#ffffff',
      scrollX: 0,
      scrollY: 0,
      foreignObjectRendering: false,
      imageSmoothingEnabled: true,
    });
    iframe.remove();

    const format = opts?.format || 'a5';
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = 4;
    const availW = pageW - margin * 2;
    const availH = pageH - margin * 2;
    let w = availW;
    let h = (canvas.height * w) / canvas.width;
    if (h > availH) {
      h = availH;
      w = (canvas.width * h) / canvas.height;
    }
    const x = (pageW - w) / 2;
    const y = (pageH - h) / 2;
    pdf.addImage(canvas.toDataURL('image/png'), 'PNG', x, y, w, h, undefined, 'NONE');
    await deliverExportPdf(pdf.output('blob'), slugFilename(title));
  } catch {
    window.alert('Could not generate PDF on this device. Please use Print → Save as PDF.');
  } finally {
    document.body.style.cursor = prevCursor;
  }
}

/** Build branded report HTML (rendered to a PDF by buildReportPdfBlob). */
export function buildReportHtml(
  title: string,
  tableHtml: string,
  opts?: { landscape?: boolean; compact?: boolean },
): string {
  const now = new Date();
  const printedAt = now.toLocaleString('en-IN');
  const heading = toHeadingCase(title || 'Report');
  const brand = resolveCompanyBrand();
  const hTitle = escapeHtmlAttr(heading);
  const hCompany = escapeHtmlAttr(brand.name);
  const hAddr = escapeHtmlAttr(brand.address);
  const hPhone = escapeHtmlAttr(brand.phone);
  const hPrinted = escapeHtmlAttr(printedAt);
  const landscape = Boolean(opts?.landscape);
  const pageWidthPx = landscape ? 1123 : 794;
  // Compact mode trims page margins + letterhead so document-style exports (e.g.
  // the loan application forms) fit more on a page and don't spill a short doc
  // onto a second sheet.
  const compact = Boolean(opts?.compact);
  // Keep readable side whitespace in every exported PDF so content never feels
  // edge-to-edge after raster-to-PDF fitting.
  const shellPadX = landscape ? 18 : compact ? 14 : 20;
  const theme = getPdfThemeColors();
  // Clean, guaranteed sans-serif stack. Plus Jakarta Sans loads from the CDN when
  // online (matches the app UI); the fallbacks are Roboto/Segoe/Arial so the PDF
  // is ALWAYS sans-serif and never drops to a serif face, even offline.
  const fontStack = '"Plus Jakarta Sans", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
  const pageRule = landscape
    ? '@page { size: A4 landscape; margin: 8mm 5mm 8mm 5mm; }'
    : compact
      ? '@page { size: A4 portrait; margin: 6mm 5mm 7mm 5mm; }'
      : '@page { size: A4 portrait; margin: 10mm 5mm 10mm 5mm; }';
  const compactCss = compact
    ? `
          .pdf-header { padding: 8px 8px 7px; margin-bottom: 8px; border-radius: 8px; }
          .pdf-logo { max-height: 44px; margin-bottom: 5px; }
          .pdf-brand { font-size: 19px; }
          .pdf-sub { font-size: 10px; margin-top: 2px; }
          .pdf-contact { font-size: 10px; margin-top: 3px; line-height: 1.4; }
          .pdf-title { font-size: 14px; margin-top: 7px; }
          .pdf-meta { font-size: 10px; margin-top: 3px; }
          .pdf-footer { margin-top: 10px; padding-top: 8px; padding-bottom: 5px; font-size: 10px; }
          .pdf-table-wrap { font-size: 11px; line-height: 1.45; }
        `
    : '';
  const tableCss = landscape
    ? `
          .pdf-table-wrap { width: 100%; overflow: hidden; }
          table.mf-landscape-report { width: 100%; table-layout: fixed; border-collapse: separate; border-spacing: 0; font-size: 11px; }
          table.mf-landscape-report th, table.mf-landscape-report td {
            border: 1px solid #94a3b8;
            padding: 7px 6px;
            text-align: left;
            vertical-align: top;
            word-wrap: break-word;
            overflow-wrap: anywhere;
            hyphens: auto;
            line-height: 1.55;
          }
          table.mf-landscape-report th {
            background: linear-gradient(180deg, ${theme.thBgFrom} 0%, ${theme.thBgTo} 100%);
            color: ${theme.thText};
            font-weight: 700;
            font-size: 11.5px;
            letter-spacing: 0.02em;
            border-bottom: 2px solid ${theme.thBorder};
          }
          table.mf-landscape-report td { font-size: 11px; color: #0f172a; white-space: pre-line; }
          table.mf-landscape-report tbody tr:nth-child(even) td { background: #f8fafc; }
        `
    : `
          .pdf-table-wrap { width: 100%; overflow: hidden; }
          table.mf-portrait-report { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 12px; table-layout: auto; }
          table.mf-portrait-report th, table.mf-portrait-report td {
            border: 1px solid #94a3b8;
            padding: 8px 6px;
            text-align: left;
            vertical-align: top;
            word-wrap: break-word;
            overflow-wrap: anywhere;
            line-height: 1.55;
          }
          table.mf-portrait-report th {
            background: linear-gradient(180deg, ${theme.thBgFrom} 0%, ${theme.thBgTo} 100%);
            color: ${theme.thText};
            font-size: 12px;
            font-weight: 700;
            vertical-align: middle;
            border-bottom: 2px solid ${theme.thBorder};
          }
          table.mf-portrait-report td { font-size: 12px; color: #0f172a; white-space: pre-line; }
          table.mf-portrait-report tbody tr:nth-child(even) td { background: #f8fafc; }
        `;

  const fullHtml = `<!DOCTYPE html>
    <html lang="en">
      <head>
        <meta charset="utf-8" />
        <meta name="viewport" content="width=${pageWidthPx}" />
        <!-- Stop mobile browsers from auto-linking phone numbers / dates as blue underlined links in the PDF. -->
        <meta name="format-detection" content="telephone=no, date=no, address=no, email=no" />
        <title>${hTitle}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
          ${pageRule}
          * { box-sizing: border-box; }
          html, body, table, th, td, div, span, p { font-family: ${fontStack}; }
          a, a:link, a:visited { color: inherit !important; text-decoration: none !important; }
          body {
            font-family: ${fontStack};
            color: #111827;
            margin: 0;
            width: ${pageWidthPx}px;
            max-width: ${pageWidthPx}px;
            overflow-x: hidden;
            -webkit-font-smoothing: antialiased;
            text-rendering: geometricPrecision;
          }
          .pdf-shell { padding: 0 ${shellPadX}px; max-width: ${pageWidthPx}px; overflow: hidden; }
          .pdf-header {
            border: 1px solid #cbd5e1;
            border-bottom: 3px solid ${theme.headerBorder};
            border-radius: 12px;
            background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
            padding: 12px 8px 10px;
            margin-bottom: 12px;
            text-align: center;
          }
          .pdf-logo { max-height: 56px; max-width: 200px; object-fit: contain; margin: 0 auto 8px; display: block; }
          .pdf-brand { font-size: 26px; font-weight: 800; color: ${theme.brand}; letter-spacing: .2px; line-height: 1.2; }
          .pdf-sub { font-size: 12px; color: #334155; margin-top: 4px; font-weight: 500; }
          .pdf-contact { font-size: 11px; color: #475569; margin-top: 6px; line-height: 1.55; }
          .pdf-title { font-size: 18px; font-weight: 700; margin-top: 12px; color: #0f172a; }
          .pdf-meta { font-size: 11.5px; color: #64748b; margin-top: 6px; font-weight: 500; }
          ${tableCss}
          ${compactCss}
          th { text-transform: capitalize; font-weight: 700; }
          .pdf-block { margin-top: 18px; }
          .pdf-section { font-size: 15px; font-weight: 800; margin: 0 0 6px; color: #0f172a; break-after: avoid; page-break-after: avoid; }
          .pdf-footer {
            border-top: 2px solid #cbd5e1;
            margin-top: 16px;
            padding-top: 12px;
            padding-bottom: 6px;
            font-size: 11px;
            font-weight: 600;
            color: #475569;
            display: flex;
            justify-content: center;
            gap: 14px;
            text-align: center;
            flex-wrap: wrap;
          }
          .pdf-sep { color: #94a3b8; font-weight: 400; }
        </style>
      </head>
      <body>
        <div class="pdf-shell">
          <div class="pdf-header">
            ${brand.logoUrl ? `<img class="pdf-logo" src="${escapeHtmlAttr(brand.logoUrl)}" alt="" crossorigin="anonymous" />` : ''}
            <div class="pdf-brand">${hCompany}</div>
            ${hAddr ? `<div class="pdf-contact">${hAddr}</div>` : ''}
            ${hPhone ? `<div class="pdf-contact">Phone: ${hPhone}</div>` : ''}
            <div class="pdf-title">${hTitle}</div>
            <div class="pdf-meta">Generated On: ${hPrinted}</div>
          </div>
          <div class="pdf-table-wrap">${tableHtml}</div>
          <div class="pdf-footer">
            <span>Confidential - Internal Use Only</span>
            <span class="pdf-sep">|</span>
            <span>Generated by ${hCompany}</span>
          </div>
        </div>
      </body>
    </html>`;
  return fullHtml;
}

export function rowsToTableHtml(rows: ExportRow[], tableClassName = '') {
  if (!rows.length) return '<p>No data available.</p>';
  const headers = Object.keys(rows[0]);
  const cls = tableClassName ? ` class="${escapeHtmlAttr(tableClassName)}"` : '';
  return `<table${cls}><thead><tr>${headers.map((h) => `<th>${escapeHtmlAttr(toHeadingCase(h))}</th>`).join('')}</tr></thead><tbody>${rows
    .map((r) => `<tr>${headers.map((h) => `<td>${escapeHtmlAttr(String(r[h] ?? ''))}</td>`).join('')}</tr>`)
    .join('')}</tbody></table>`;
}

/** One titled table inside a combined print or export. */
export type ReportBlock = { title: string; rows: ExportRow[] };

function blocksWithPlaceholder(blocks: ReportBlock[]): ReportBlock[] {
  return blocks.map((block) => ({
    title: block.title,
    rows: block.rows.length ? block.rows : [{ Note: 'No records for this date range' }],
  }));
}

/** Several report tables, each with its own heading, in one document. */
export function blocksToTableHtml(blocks: ReportBlock[], tableClassName = '') {
  return blocksWithPlaceholder(blocks)
    .map(
      (block) =>
        `<section class="pdf-block"><h2 class="pdf-section">${escapeHtmlAttr(block.title)}</h2>${rowsToTableHtml(block.rows, tableClassName)}</section>`
    )
    .join('');
}

/** One Excel file with every report stacked, each under its own heading. */
export function exportExcelBlocks(filename: string, blocks: ReportBlock[]) {
  const filled = blocksWithPlaceholder(blocks);
  const theme = getPdfThemeColors();
  const body = filled
    .map((block) => {
      const headers = Object.keys(block.rows[0]);
      return `<h3>${escapeHtmlAttr(block.title)}</h3><table border="1"><thead><tr>${headers
        .map((h) => `<th style="background:${theme.excelHeader};color:#fff;font-weight:bold;">${escapeHtmlAttr(toHeadingCase(h))}</th>`)
        .join('')}</tr></thead><tbody>${block.rows
        .map((row) => `<tr>${headers.map((h) => `<td>${escapeHtmlAttr(String(row[h] ?? ''))}</td>`).join('')}</tr>`)
        .join('')}</tbody></table>`;
    })
    .join('<br/>');
  const safeBase = filename.replace(/\.[^.]+$/, '') || 'report';
  const workbook = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
      <head><meta charset="utf-8" /><!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>Reports</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]--></head>
      <body>${body}</body>
    </html>
  `;
  const blob = new Blob(['\uFEFF', workbook], { type: 'application/vnd.ms-excel;charset=utf-8;' });
  void downloadFile(blob, `${safeBase}.xls`);
}

/** Convenience: build the branded table HTML and print, in one call. */
export function exportRowsToPdf(title: string, rows: ExportRow[], opts?: { landscape?: boolean }) {
  const landscape = resolveLandscape(rows, opts?.landscape);
  exportHtmlToPdf(title, rowsToTableHtml(rows, tableClassForLayout(landscape)), { ...opts, landscape });
}

/** Open the browser print dialog for the same branded report as Export File. */
export function printReport(title: string, rows: ExportRow[], opts?: { landscape?: boolean }) {
  const landscape = resolveLandscape(rows, opts?.landscape);
  const tableHtml = rowsToTableHtml(rows, tableClassForLayout(landscape));
  // Phone and the installed app mishandle window.print(); hand them the same PDF.
  if (isMobileExportEnvironment()) {
    exportHtmlToPdf(title, tableHtml, { landscape });
    return;
  }
  const html = buildReportHtml(title, tableHtml, { landscape });
  const win = window.open('', '_blank');
  if (!win) {
    window.alert('Allow pop-ups to print this report.');
    return;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
  const run = () => {
    win.focus();
    win.print();
  };
  win.addEventListener('load', run, { once: true });
  window.setTimeout(run, 400);
}

async function mountReportIframe(fullHtml: string, pageWidthPx = 794): Promise<HTMLIFrameElement> {
  const IFRAME_ID = '__edgefin_share_iframe__';
  document.getElementById(IFRAME_ID)?.remove();
  const iframe = document.createElement('iframe');
  iframe.id = IFRAME_ID;
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText =
    `position:fixed;left:-10000px;top:0;width:${pageWidthPx}px;min-height:1123px;border:0;background:#fff;`;
  document.body.appendChild(iframe);

  await new Promise<void>((resolve) => {
    const doc = iframe.contentWindow?.document;
    if (!doc) {
      resolve();
      return;
    }
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    // Wait for embedded web fonts to finish loading before we hand off to
    // html2canvas — otherwise the capture renders with the fallback face and the
    // PDF font "changes". Fall back to a timeout if the Font Loading API is
    // missing or fonts can't be fetched (offline).
    const waitForFonts = () => {
      const fdoc = iframe.contentWindow?.document as
        | (Document & { fonts?: { ready?: Promise<unknown> } })
        | undefined;
      const ready = fdoc?.fonts?.ready;
      if (ready && typeof ready.then === 'function') {
        ready.then(() => window.setTimeout(done, 40)).catch(() => window.setTimeout(done, 40));
      } else {
        window.setTimeout(done, 120);
      }
    };
    iframe.addEventListener('load', waitForFonts, { once: true });
    doc.open();
    doc.write(fullHtml);
    doc.close();
    window.setTimeout(waitForFonts, 120);
    // Hard safety net so we never hang if load never fires.
    window.setTimeout(done, 1200);
  });

  return iframe;
}

/** Render report HTML to a PDF blob (for native share sheet). */
export async function buildReportPdfBlob(
  title: string,
  tableHtml: string,
  opts?: { landscape?: boolean; compact?: boolean; singlePage?: boolean }
): Promise<Blob> {
  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([import('html2canvas'), import('jspdf')]);
  const landscape = Boolean(opts?.landscape);
  const pageWidthPx = landscape ? 1123 : 794;
  const fullHtml = buildReportHtml(title, tableHtml, opts);
  const iframe = await mountReportIframe(fullHtml, pageWidthPx);
  const doc = iframe.contentDocument;
  const body = doc?.body;
  if (!body || !doc) throw new Error('Could not render report for sharing.');

  const contentHeight = Math.max(body.scrollHeight, doc.documentElement.scrollHeight, landscape ? 794 : 1123);
  iframe.style.height = `${contentHeight}px`;
  body.style.width = `${pageWidthPx}px`;
  body.style.maxWidth = `${pageWidthPx}px`;
  body.style.overflow = 'hidden';

  await new Promise<void>((resolve) => window.setTimeout(() => resolve(), isMobileExportEnvironment() ? 180 : 80));

  /*
   * Root cause of soft / blurry PDFs on zoom:
   * 1) Mobile used low html2canvas scale (~1.75–2.25)
   * 2) Mobile single-page docs were saved as JPEG @ 0.82 (destroys text + QR edges)
   * Fix: high DPI capture + lossless PNG for documents; near-max JPEG only for long reports.
   */
  const MAX_CANVAS_PX = 24000;
  const mobile = isMobileExportEnvironment();
  const singlePage = Boolean(opts?.singlePage);
  const dpr =
    typeof window !== 'undefined' && Number.isFinite(window.devicePixelRatio)
      ? Math.max(1, window.devicePixelRatio)
      : 1;
  const targetScale = singlePage
    ? Math.max(4, Math.min(5, 2 + dpr))
    : landscape
      ? mobile
        ? 3
        : 3.5
      : mobile
        ? 3
        : 3.5;
  const scale = Math.max(2.5, Math.min(targetScale, MAX_CANVAS_PX / Math.max(1, contentHeight)));

  const canvas = await html2canvas(body, {
    scale,
    useCORS: true,
    allowTaint: true,
    logging: false,
    backgroundColor: '#ffffff',
    width: pageWidthPx,
    windowWidth: pageWidthPx,
    height: contentHeight,
    windowHeight: contentHeight,
    scrollX: 0,
    scrollY: 0,
    foreignObjectRendering: false,
    imageSmoothingEnabled: true,
    onclone: (_clonedDoc, clonedEl) => {
      // Keep QR / barcode bitmaps crisp — nearest-neighbour, no soft blur.
      clonedEl.querySelectorAll('img[data-hd-qr], canvas[data-hd-qr], .hd-qr').forEach((node) => {
        const el = node as HTMLElement;
        el.style.setProperty('image-rendering', 'pixelated');
        el.style.setProperty('image-rendering', 'crisp-edges');
      });
    },
  });

  const orientation = landscape ? 'landscape' : 'portrait';
  const pdf = new jsPDF({ orientation, unit: 'pt', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  // Single-page forms + any page with HD QR: always lossless PNG.
  // Long multi-page reports: high-quality JPEG to stay under WebView memory limits.
  const hasHdQr = Boolean(body.querySelector('img[data-hd-qr], canvas[data-hd-qr], .hd-qr'));
  const usePng = singlePage || hasHdQr || canvas.height < 10000;
  const imgFormat = usePng ? 'PNG' : 'JPEG';
  const imgData =
    imgFormat === 'PNG'
      ? canvas.toDataURL('image/png')
      : canvas.toDataURL('image/jpeg', 0.95);
  const compress = imgFormat === 'PNG' ? 'NONE' : 'MEDIUM';

  // Single-page docs (e.g. loan application form) are scaled to fit on ONE A4
  // sheet so the signature block never spills onto a second page. A safe margin
  // keeps the letterhead + footer ("Confidential…") clear of the page edge so
  // nothing is clipped.
  if (singlePage) {
    const margin = 16; // pt — ~5.6mm breathing room on every side
    const availW = pageWidth - margin * 2;
    const availH = pageHeight - margin * 2;
    let w = availW;
    let h = (canvas.height * w) / canvas.width;
    if (h > availH) {
      h = availH;
      w = (canvas.width * h) / canvas.height;
    }
    const x = (pageWidth - w) / 2;
    const y = (pageHeight - h) / 2;
    pdf.addImage(imgData, imgFormat, x, y, w, h, undefined, compress);
    iframe.remove();
    return pdf.output('blob');
  }

  const imgWidth = pageWidth;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;
  let heightLeft = imgHeight;
  let position = 0;
  pdf.addImage(imgData, imgFormat, 0, position, imgWidth, imgHeight, undefined, compress);
  heightLeft -= pageHeight;
  // Tolerance (~1mm) so a rounding sliver left over on the last page doesn't add
  // a whole extra blank sheet.
  const PAGE_EPSILON = 3;
  while (heightLeft > PAGE_EPSILON) {
    position -= pageHeight;
    pdf.addPage();
    pdf.addImage(imgData, imgFormat, 0, position, imgWidth, imgHeight, undefined, compress);
    heightLeft -= pageHeight;
  }

  iframe.remove();
  return pdf.output('blob');
}

/** Lightweight PDF fallback when html2canvas is unavailable in a mobile WebView. */
async function buildSimplePdfBlob(
  title: string,
  tableHtml: string,
  opts?: { landscape?: boolean },
): Promise<Blob> {
  const { jsPDF } = await import('jspdf');
  const landscape = Boolean(opts?.landscape);
  const pdf = new jsPDF({ orientation: landscape ? 'landscape' : 'portrait', unit: 'pt', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const margin = 16;
  const maxWidth = pageWidth - margin * 2;
  let y = margin;

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(14);
  pdf.text(toHeadingCase(title || 'Report'), margin, y);
  y += 24;

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  const plain = tableHtml
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/tr>/gi, '\n')
    .replace(/<\/t[hd]>/gi, ' | ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .trim();

  const lines = pdf.splitTextToSize(plain || 'No data available.', maxWidth);
  for (const line of lines) {
    if (y > pdf.internal.pageSize.getHeight() - margin) {
      pdf.addPage();
      y = margin;
    }
    pdf.text(line, margin, y);
    y += 11;
  }

  return pdf.output('blob');
}

/**
 * Share report as PDF via the device share sheet (WhatsApp, etc.) — no Business API.
 * Falls back to wa.me + PDF download when native file share is unavailable.
 */
export async function shareReportViaWhatsApp(
  title: string,
  rows: ExportRow[],
  opts?: { landscape?: boolean; whatsappPhone?: string; pdfFilename?: string; tableHtml?: string }
) {
  document.querySelectorAll('details[open]').forEach((el) => el.removeAttribute('open'));
  if (!rows.length && !opts?.tableHtml) return;

  const landscape = resolveLandscape(rows, opts?.landscape);
  const tableHtml = opts?.tableHtml || rowsToTableHtml(rows, tableClassForLayout(landscape));
  const brand = resolveCompanyBrand();
  const filename = opts?.pdfFilename || slugFilename(title);
  const shareText = `${brand.name} — ${toHeadingCase(title)}\nGenerated ${new Date().toLocaleString('en-IN')}`;

  let blob: Blob;
  try {
    blob = await buildReportPdfBlob(title, tableHtml, { landscape });
  } catch {
    if (isMobileExportEnvironment()) {
      window.alert('Could not generate PDF for sharing. Please try Export PDF instead.');
    } else {
      exportHtmlToPdf(title, tableHtml, { landscape });
    }
    return;
  }

  // Native app: open the OS share sheet with the PDF file attached (WhatsApp is a
  // target). This is the reliable path inside the APK.
  if (isNativeApp()) {
    try {
      const [{ Filesystem, Directory }, { Share }] = await Promise.all([
        import('@capacitor/filesystem'),
        import('@capacitor/share'),
      ]);
      const base64 = await blobToBase64(blob);
      const written = await Filesystem.writeFile({ path: filename, data: base64, directory: Directory.Cache });
      await Share.share({ title, text: shareText, files: [written.uri] });
      return;
    } catch (err) {
      if (err instanceof Error && /cancel/i.test(err.message)) return;
      // fall through to web strategies
    }
  }

  const file = new File([blob], filename, { type: 'application/pdf' });
  const nav = typeof navigator !== 'undefined' ? (navigator as Navigator & { canShare?: (d: unknown) => boolean }) : null;
  if (nav?.share && (!nav.canShare || nav.canShare({ files: [file] }))) {
    try {
      await nav.share({ title, text: shareText, files: [file] });
      return;
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
    }
  }

  downloadBlob(blob, filename);
  const phone = String(opts?.whatsappPhone || '')
    .replace(/\D/g, '')
    .replace(/^0/, '');
  const waPhone = phone.length === 10 ? `91${phone}` : phone;
  const msg = encodeURIComponent(`${shareText}\n\n(PDF saved on your device — attach it in WhatsApp.)`);
  const url = waPhone ? `https://wa.me/${waPhone}?text=${msg}` : `https://wa.me/?text=${msg}`;
  window.open(url, '_blank', 'noopener,noreferrer');
}

export function ExportMenu({
  title,
  excelFilename,
  rows,
  sections,
  className = 'ml-auto inline-block shrink-0',
  disabled = false,
  landscape,
  whatsappPhone,
}: {
  title: string;
  excelFilename: string;
  rows: ExportRow[];
  /** When set, Print and every export include all of these reports in one file. */
  sections?: ReportBlock[];
  className?: string;
  disabled?: boolean;
  /** Optional override — omit to auto-detect portrait vs landscape from column width. */
  landscape?: boolean;
  /** Optional customer number — pre-fills WhatsApp when native share is unavailable. */
  whatsappPhone?: string;
}) {
  const pdfFilename = slugFilename(title);
  const useLandscape = sections?.length ? landscape !== false : resolveLandscape(rows, landscape);
  const tableClass = tableClassForLayout(useLandscape);
  const reportHtml = sections?.length ? blocksToTableHtml(sections, tableClass) : rowsToTableHtml(rows, tableClass);
  const canExport = sections?.length ? sections.length > 0 : rows.length > 0;
  const [open, setOpen] = useState(false);
  const [useSheet, setUseSheet] = useState(false);
  const [exporting, setExporting] = useState<'pdf' | 'whatsapp' | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const sync = () => setUseSheet(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    if (!open || useSheet) return;
    const onDoc = (ev: MouseEvent) => {
      const target = ev.target as Node;
      if (rootRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, useSheet]);

  useEffect(() => {
    if (!exporting) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [exporting]);

  const closeMenu = useCallback(() => setOpen(false), []);

  const runPdfExport = () => {
    if (!canExport) {
      window.alert('No data to export.');
      return;
    }
    closeMenu();
    setExporting('pdf');
    void exportHtmlToPdf(title, reportHtml, { landscape: useLandscape }).finally(() => {
      setExporting(null);
    });
  };

  const runExcelExport = () => {
    if (!canExport) {
      window.alert('No data to export.');
      return;
    }
    closeMenu();
    if (sections?.length) exportExcelBlocks(excelFilename, sections);
    else exportExcel(excelFilename, rows);
  };

  const runWhatsAppShare = () => {
    if (!canExport) {
      window.alert('No data to export.');
      return;
    }
    closeMenu();
    setExporting('whatsapp');
    void shareReportViaWhatsApp(title, rows.length ? rows : [{ Note: title }], {
      landscape: useLandscape,
      whatsappPhone,
      pdfFilename,
      tableHtml: reportHtml,
    }).finally(() => {
      setExporting(null);
    });
  };

  const menuItems = (
    <>
      <button
        type="button"
        className="block w-full rounded-lg px-3 py-2.5 text-left text-sm font-medium text-zinc-700 transition hover:bg-zinc-100 active:bg-zinc-100 disabled:opacity-50"
        disabled={disabled || !!exporting}
        onClick={runPdfExport}
      >
        <span className="inline-flex items-center gap-2">
          <span aria-hidden>📄</span>
          <span>Export Pdf</span>
        </span>
      </button>
      <button
        type="button"
        className="mt-1 block w-full rounded-lg px-3 py-2.5 text-left text-sm font-medium text-zinc-700 transition hover:bg-zinc-100 active:bg-zinc-100 disabled:opacity-50"
        disabled={disabled || !!exporting}
        onClick={runExcelExport}
      >
        <span className="inline-flex items-center gap-2">
          <span aria-hidden>📊</span>
          <span>Export Excel</span>
        </span>
      </button>
      <button
        type="button"
        className="mt-1 block w-full rounded-lg px-3 py-2.5 text-left text-sm font-medium text-emerald-800 transition hover:bg-emerald-50 active:bg-emerald-100 disabled:opacity-50"
        disabled={disabled || !!exporting}
        onClick={runWhatsAppShare}
      >
        <span className="inline-flex items-center gap-2">
          <span aria-hidden>💬</span>
          <span>Share via WhatsApp</span>
        </span>
      </button>
    </>
  );

  const sheet =
    open && useSheet && typeof document !== 'undefined'
      ? createPortal(
          <div className="fixed inset-0 z-[120]" role="presentation">
            <button
              type="button"
              className="absolute inset-0 bg-zinc-950/40"
              aria-label="Close export menu"
              onClick={closeMenu}
            />
            <div
              className="absolute inset-x-0 bottom-0 rounded-t-2xl border border-zinc-200 bg-white p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-2xl"
              role="menu"
              aria-label="Export options"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-zinc-300" aria-hidden />
              <p className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-zinc-500">Export</p>
              {menuItems}
            </div>
          </div>,
          document.body,
        )
      : null;

  const exportingOverlay =
    exporting && typeof document !== 'undefined'
      ? createPortal(
          <div
            className="fixed inset-0 z-[140] flex items-center justify-center bg-zinc-950/50 p-6"
            role="alertdialog"
            aria-busy="true"
            aria-live="polite"
          >
            <div className="w-full max-w-xs rounded-2xl border border-zinc-200 bg-white px-5 py-6 text-center shadow-2xl">
              <div
                className="mx-auto h-10 w-10 animate-spin rounded-full border-[3px] border-emerald-200 border-t-emerald-600"
                aria-hidden
              />
              <p className="mt-4 text-sm font-semibold text-zinc-900">
                {exporting === 'whatsapp' ? 'Preparing share…' : 'Generating PDF…'}
              </p>
              <p className="mt-1 text-xs text-zinc-500">Please wait — this may take a few seconds on mobile.</p>
              <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-zinc-100">
                <div className="h-full w-1/2 animate-pulse rounded-full bg-emerald-500" />
              </div>
            </div>
          </div>,
          document.body,
        )
      : null;

  const runPrint = () => {
    if (!canExport) {
      window.alert('No data to print.');
      return;
    }
    closeMenu();
    if (isMobileExportEnvironment()) {
      void exportHtmlToPdf(title, reportHtml, { landscape: useLandscape });
      return;
    }
    const html = buildReportHtml(title, reportHtml, { landscape: useLandscape });
    const win = window.open('', '_blank');
    if (!win) {
      window.alert('Allow pop-ups to print this report.');
      return;
    }
    win.document.open();
    win.document.write(html);
    win.document.close();
    const run = () => {
      win.focus();
      win.print();
    };
    win.addEventListener('load', run, { once: true });
    window.setTimeout(run, 400);
  };

  return (
    <div className={`flex flex-wrap items-center justify-end gap-2 ${className}`}>
      <button
        type="button"
        className={`app-btn-secondary ${disabled || exporting ? 'pointer-events-none opacity-50' : ''}`}
        disabled={disabled || !!exporting}
        onClick={runPrint}
      >
        Print
      </button>
      <div ref={rootRef} className="relative">
      <button
        type="button"
        className={`app-btn-secondary ${disabled || exporting ? 'pointer-events-none opacity-50' : ''}`}
        disabled={disabled || !!exporting}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((v) => !v)}
      >
        {exporting ? 'Generating…' : 'Export File'}
      </button>
      {open && !useSheet && (
        <div
          className="absolute right-0 z-[80] mt-2 w-52 max-w-[min(13rem,calc(100vw-1.5rem))] overflow-hidden rounded-xl border border-zinc-200 bg-white p-1 shadow-xl"
          role="menu"
          aria-label="Export options"
        >
          {menuItems}
        </div>
      )}
      {sheet}
      {exportingOverlay}
      </div>
    </div>
  );
}
